import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Collapse, Steps, Button, Tag, Typography, message, Divider, Input, Space, List, Avatar } from 'antd';
import { CheckCircleOutlined, CommentOutlined, UserOutlined } from '@ant-design/icons';
import api from '../../api/axios';

const { Panel } = Collapse;
const { Text, Paragraph, Title } = Typography;

const ProgressTracker = () => {
    const { matchId } = useParams();
    const [commitments, setCommitments] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // State to hold comments being typed, keyed by commitment ID
    const [commentTexts, setCommentTexts] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const fetchProgress = async () => {
        setLoading(true);
        try {
            // Ensure backend eager loads 'comments.user'
            const res = await api.get(`/mentor/matches/${matchId}/commitments`);
            setCommitments(res.data);
        } catch (e) {
            message.error('Failed to load progress data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchProgress(); }, [matchId]);

    const handleCommentChange = (id, value) => {
        setCommentTexts(prev => ({ ...prev, [id]: value }));
    };

    const submitComment = async (commitmentId) => {
        const content = commentTexts[commitmentId];
        if (!content || !content.trim()) return;

        setSubmitting(true);
        try {
            await api.post(`/commitments/${commitmentId}/comments`, { content });
            message.success('Comment added!');
            handleCommentChange(commitmentId, ''); // Clear input
            fetchProgress(); // Refresh to show new comment
        } catch (e) {
            message.error('Failed to submit comment');
        } finally {
            setSubmitting(false);
        }
    };

    const confirmMilestone = async (milestoneId) => {
        try {
            await api.patch(`/mentor/milestones/${milestoneId}/confirm`);
            message.success('Milestone confirmed!');
            fetchProgress();
        } catch (e) {
            message.error('Only completed milestones can be confirmed');
        }
    };

    return (
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <Title level={3}>Mentee Progress Tracking</Title>
            <Divider />
            
            <Collapse accordion loading={loading}>
                {commitments.map((commitment) => (
                    <Panel 
                        header={
                            <Space>
                                <Text strong>{commitment.title}</Text>
                                <Tag color={commitment.status === 'open' ? 'orange' : 'green'}>
                                    {commitment.status.toUpperCase()}
                                </Tag>
                            </Space>
                        } 
                        key={commitment.id}
                    >
                        <Paragraph>{commitment.description}</Paragraph>
                        
                        <Steps direction="vertical" size="small" current={-1}>
                            {commitment.milestones.map((m) => (
                                <Steps.Step
                                    key={m.id}
                                    title={m.title}
                                    description={
                                        <div style={{ marginTop: 10 }}>
                                            <Tag color={m.status === 'confirmed_by_mentor' ? 'green' : m.status === 'completed_by_mentee' ? 'blue' : 'default'}>
                                                {m.status.replace(/_/g, ' ').toUpperCase()}
                                            </Tag>
                                            {m.status === 'completed_by_mentee' && (
                                                <Button 
                                                    size="small" 
                                                    type="primary" 
                                                    icon={<CheckCircleOutlined />}
                                                    onClick={() => confirmMilestone(m.id)}
                                                >
                                                    Confirm
                                                </Button>
                                            )}
                                        </div>
                                    }
                                />
                            ))}
                        </Steps>
                        
                        <Divider />
                        
                        {/* Display existing comments */}
                        <List
                            header={<Text strong><CommentOutlined /> Feedback History</Text>}
                            dataSource={commitment.comments || []}
                            locale={{ emptyText: 'No feedback yet' }}
                            renderItem={item => (
                                <List.Item>
                                    <List.Item.Meta
                                        avatar={<Avatar icon={<UserOutlined />} />}
                                        title={<Text strong>{item.user?.name}</Text>}
                                        description={item.content}
                                    />
                                    <Text type="secondary" style={{ fontSize: '11px' }}>
                                        {new Date(item.created_at).toLocaleDateString()}
                                    </Text>
                                </List.Item>
                            )}
                        />

                        <div style={{ marginTop: 20 }}>
                            <Input.TextArea 
                                rows={2} 
                                placeholder="Add feedback for your mentee..." 
                                value={commentTexts[commitment.id] || ''}
                                onChange={(e) => handleCommentChange(commitment.id, e.target.value)}
                            />
                            <Button 
                                type="primary" 
                                style={{ marginTop: 10, backgroundColor: '#FE1F08', border: 'none' }}
                                onClick={() => submitComment(commitment.id)}
                                loading={submitting}
                            >
                                Submit Feedback
                            </Button>
                        </div>
                    </Panel>
                ))}
            </Collapse>
        </div>
    );
};

export default ProgressTracker;